/**
 * SunoCoverIndexContext — общесистемный индекс обложек Suno-треков.
 *
 * Зачем: старые GenerationCard (в RecentGenerations, чат-истории, и любых
 * других местах где рендерится audio-карточка по сохранённому ранее snapshot'у)
 * не содержат `previewUrl` — на момент сохранения мы не читали image_url
 * из API. Сами треки в БД `suno_tracks` обложку имеют, просто frontend её
 * не знал.
 *
 * Решение без миграции localStorage: при старте приложения загружаем список
 * треков юзера (`GET /music/tracks?limit=100`), строим map `audio_url → image_url`,
 * и раздаём через хук `useSunoCoverIndex()`. Компоненты, которые рендерят
 * audio-карточку, для случая когда `previewUrl` пуст — резолвят обложку
 * через этот индекс.
 *
 * Что НЕ делаем:
 *   - Не мутируем localStorage / сохранённые snapshot'ы (fragile, лишняя работа)
 *   - Не блокируем рендер на загрузку (map просто пуст пока не подгрузилось)
 *   - Не загружаем для незалогиненных юзеров
 *
 * Refresh:
 *   - При первом mount (если user есть)
 *   - При фокусе вкладки если прошло >5 мин (юзер мог сгенерировать новый трек)
 *   - Явно через `refresh()` — например, после handleGenerate
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { getUserTracks } from '../api/music'
import { useAuth } from './AuthContext'

interface SunoCoverIndexValue {
	/** По audio URL вернуть image_url обложки. null если не в индексе. */
	getCover: (audioUrl: string | undefined | null) => string | null
	/** Принудительный refresh индекса. Можно звать после успешной генерации. */
	refresh: () => Promise<void>
}

const SunoCoverIndexContext = createContext<SunoCoverIndexValue | null>(null)

const STALE_MS = 5 * 60 * 1000 // 5 минут — refresh при возврате на вкладку

export function SunoCoverIndexProvider({ children }: { children: ReactNode }) {
	const { user } = useAuth()
	const [map, setMap] = useState<Map<string, string>>(new Map())
	const loadingRef = useRef(false)
	const lastLoadRef = useRef(0)

	const load = useCallback(async () => {
		if (!user) return
		if (loadingRef.current) return
		loadingRef.current = true
		try {
			const { tracks } = await getUserTracks({ limit: 100 })
			const newMap = new Map<string, string>()
			for (const t of tracks) {
				if (!t.image_url) continue
				// Мапим оба варианта URL (финальный и stream) чтобы матчились
				// старые snapshot'ы с любым из них.
				if (t.audio_url) newMap.set(t.audio_url, t.image_url)
				if (t.stream_audio_url) newMap.set(t.stream_audio_url, t.image_url)
			}
			setMap(newMap)
			lastLoadRef.current = Date.now()
		} catch {
			// silent — индекс остаётся пустым, карточки покажут placeholder
		} finally {
			loadingRef.current = false
		}
	}, [user])

	// Первая загрузка при появлении user. Перезагрузка при login/logout.
	useEffect(() => {
		if (user) {
			load()
		} else {
			setMap(new Map())
			lastLoadRef.current = 0
		}
	}, [user, load])

	// Refresh при возврате на вкладку если индекс устарел.
	useEffect(() => {
		function onFocus() {
			if (!user) return
			if (Date.now() - lastLoadRef.current > STALE_MS) {
				load()
			}
		}
		window.addEventListener('focus', onFocus)
		return () => window.removeEventListener('focus', onFocus)
	}, [user, load])

	const getCover = useCallback(
		(audioUrl: string | undefined | null) => {
			if (!audioUrl) return null
			return map.get(audioUrl) ?? null
		},
		[map],
	)

	const value = useMemo<SunoCoverIndexValue>(
		() => ({ getCover, refresh: load }),
		[getCover, load],
	)

	return (
		<SunoCoverIndexContext.Provider value={value}>
			{children}
		</SunoCoverIndexContext.Provider>
	)
}

/** Хук для чтения индекса. Если Provider не смонтирован — безопасный fallback. */
export function useSunoCoverIndex(): SunoCoverIndexValue {
	const ctx = useContext(SunoCoverIndexContext)
	if (!ctx) {
		return {
			getCover: () => null,
			refresh: async () => {},
		}
	}
	return ctx
}
